// Shared fetch-based SSE plumbing used by both useEventStream (persistent,
// render-driven, auto-reconnecting) and subscribeStrategySimulationRun
// (one-shot, imperative, resolves on a terminal event). Native EventSource
// can't be used by either caller: these endpoints require a bearer-token
// Authorization header, which EventSource has no way to set.
import { createSseParser, type SseEvent } from './sseParser';

export interface OpenSseStreamOptions {
  signal?: AbortSignal;
}

/** Opens a fetch-based SSE connection with the bearer-auth headers every SSE
 * consumer in this app needs. Does not throw on non-2xx — callers inspect
 * `response.status`/`response.ok` themselves (e.g. to special-case a 504). */
export function openSseStream(url: string, options: OpenSseStreamOptions = {}): Promise<Response> {
  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'text/event-stream',
    },
    credentials: 'include',
    signal: options.signal,
  });
}

/**
 * Reads an already-opened SSE `Response` body, invoking `onEvent` for each
 * parsed event in order. Returns once the stream ends naturally, or as soon
 * as `onEvent` returns `true` — an early-stop signal for one-shot callers
 * that only need to read up to a specific event (e.g. a terminal status).
 */
export async function readSseStream(
  response: Response,
  onEvent: (event: SseEvent) => boolean | void,
): Promise<void> {
  if (!response.body) {
    throw new Error(`HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();

  while (true) {
    const { done, value } = await reader.read();
    if (done) return;

    for (const event of parser.push(decoder.decode(value, { stream: true }))) {
      if (onEvent(event) === true) return;
    }
  }
}
